import { CtaVariant } from 'components/helpers/Atoms/Cta/CtaButton';
import { useBrandAndTheme } from 'components/helpers/ContextProvider/BrandAndThemeContext';
import { brandMap, BrandType, themeMap, ThemeType, globalMap, Brands, Themes } from 'lib/themes';

/** Expected to be a class name that resolves to a button type, e.g. 'component-feature-button-color-1'. */
export type CtaComponentClass = keyof BrandType | keyof ThemeType;

// Cache all the mappings so we don't re-calculate them for each component.
const CachedFullMapping: Record<Brands, Record<Themes, Record<string, string>>> = {} as Record<
  Brands,
  Record<Themes, Record<string, string>>
>;

// Mapping of the theme enum to the theme type.
export function useCtaComponentClass(ctaComponentClass: CtaComponentClass | undefined): CtaVariant {
  const { brand, theme } = useBrandAndTheme();

  // Resolve the mapping.
  const mapping = resolveVariableMapping(brand, theme);

  const style =
    (ctaComponentClass ? (mapping[ctaComponentClass] as CtaVariant) : undefined) || 'brand';

  return style;
}

/**
 * Resolves variable references in the mapping to resolve the final value.
 * e.g. var(--general-theme-brand-primary-onBG-bg)
 * @param mapping - The mapping to resolve.
 * @returns The resolved mapping.
 */
function resolveVariableMapping(brand: Brands, theme: Themes) {
  if (CachedFullMapping?.[brand]?.[theme]) {
    return CachedFullMapping[brand][theme];
  }
  // Merge the global, brand, and theme objects.
  const mapping: Record<string, string> = {
    ...globalMap.GlobalMode1,
    ...brandMap[brand],
    ...themeMap[theme],
  };

  let changed = false;
  // Max depth to prevent infinite loops.
  let maxDepth = 10;
  do {
    maxDepth--;
    changed = false;
    Object.keys(mapping).forEach((key) => {
      const value = mapping[key];
      if (!value) {
        return;
      }
      const match = value.match(/var\(--(.*)\)/)?.[1];
      if (match) {
        mapping[key] = mapping[match];
        changed = true;
      }
    });
  } while (changed && maxDepth > 0);
  if (maxDepth <= 0) {
    console.error(mapping);
    throw new Error('Max depth reached for resolving variable references');
  }

  if (!CachedFullMapping?.[brand]) {
    CachedFullMapping[brand] = {} as Record<Themes, Record<string, string>>;
  }

  CachedFullMapping[brand][theme] = mapping;
  return mapping;
}
